/**
 * Chat Input Component - "Input Dock" Design
 * Glass morphism design with prompt enhancer and action buttons
 * Validates: Requirements 13.1, 18.1
 */
import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  promptEnhancerEnabled: boolean;
  onToggleEnhancer: () => void;
}

export function ChatInput({
  onSend,
  disabled = false,
  promptEnhancerEnabled,
  onToggleEnhancer,
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [message]);

  const handleSubmit = () => {
    const trimmed = message.trim();
    if (trimmed && !disabled) {
      onSend(trimmed);
      setMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      className="px-8 py-6"
      style={{
        background: `linear-gradient(to top, ${THEME.bg}, transparent)`,
      }}
    >
      <div className="max-w-3xl mx-auto">
        {/* Input Dock Container */}
        <div
          className="border rounded-3xl p-3 transition-all duration-200"
          style={{
            backgroundColor: THEME.glassLight,
            backdropFilter: 'blur(10px)',
            borderColor: THEME.border,
            boxShadow: THEME.shadowXl,
          }}
        >
          <div className="flex items-end gap-3">
            {/* Left Actions */}
            <div className="flex gap-2">
              <button
                type="button"
                className="p-2.5 rounded-xl transition-all duration-200 border hover:border-white/20"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  borderColor: THEME.border,
                  color: THEME.textMuted,
                }}
                title="Attach RAG documents"
              >
                <Icons.Database size={18} />
              </button>
              <button
                type="button"
                className="p-2.5 rounded-xl transition-all duration-200 border hover:border-white/20"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  borderColor: THEME.border,
                  color: THEME.textMuted,
                }}
                title="Attach file"
              >
                <Icons.Paperclip size={18} />
              </button>
            </div>

            {/* Textarea */}
            <div className="flex-1">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Command the AI..."
                disabled={disabled}
                rows={1}
                className="w-full bg-transparent text-white resize-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  minHeight: '24px',
                  maxHeight: '120px',
                  fontSize: '15px',
                  lineHeight: '1.5',
                  color: THEME.textMain,
                }}
              />
            </div>

            {/* Right Actions */}
            <div className="flex gap-2">
              {/* Enhance Button */}
              <button
                type="button"
                onClick={onToggleEnhancer}
                disabled={!message.trim()}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border ${
                  promptEnhancerEnabled ? 'animate-pulse-glow' : ''
                }`}
                style={{
                  borderColor: message.trim()
                    ? promptEnhancerEnabled
                      ? THEME.accentBorder
                      : THEME.border
                    : THEME.border,
                  backgroundColor: message.trim()
                    ? promptEnhancerEnabled
                      ? THEME.accentSoft
                      : 'transparent'
                    : 'transparent',
                  color: message.trim()
                    ? promptEnhancerEnabled
                      ? THEME.accentColor
                      : THEME.textMuted
                    : THEME.textMuted,
                  opacity: message.trim() ? 1 : 0.5,
                }}
              >
                <Icons.Sparkles size={16} />
                <span>Enhance</span>
              </button>

              {/* Send Button */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={disabled || !message.trim()}
                className="p-2.5 rounded-xl transition-all duration-200"
                style={{
                  background: message.trim() ? THEME.accent : 'rgba(255,255,255,0.05)',
                  color: THEME.textMain,
                  boxShadow: message.trim() ? THEME.shadowAccent : 'none',
                  opacity: message.trim() ? 1 : 0.5,
                  cursor: message.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                <Icons.Send size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="text-center mt-3 text-xs"
          style={{ color: THEME.textMuted }}
        >
          Powered by Nexus Intelligence Engine
        </div>
      </div>
    </div>
  );
}
